import React, { useState, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { CheckCircle, BarChart3, FileText, ArrowRight, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Actions as IMPORT_ACTIONS } from '../../store/actions/intelligentImportActions';
import { PIPELINE_STATUSES } from '../../constants/importConstants';

const StagingSubmit = ({ importId, job, commitImport, loading }) => {
  const history = useHistory();
  const [committed, setCommitted] = useState(
    job?.pipelineStatus === PIPELINE_STATUSES.COMPLETE || job?.pipelineStatus === PIPELINE_STATUSES.PARTIAL_COMPLETE,
  );

  const handleCommit = useCallback(async () => {
    const result = await commitImport(importId);
    if (result) setCommitted(true);
  }, [importId, commitImport]);

  const stats = useMemo(() => ({
    total: job?.totalRows || job?.total_rows || 0,
    matched: job?.matchedRows || job?.matched_rows || 0,
    committed: job?.committedRows || job?.committed_rows || job?.committed || 0,
    duplicates: job?.duplicateRows || job?.duplicate_rows || 0,
    filtered: job?.filteredRows || job?.filtered_rows || 0,
    errors: job?.commitErrors || job?.commit_errors || 0,
  }), [job]);

  if (committed) {
    return (
      <div className='flex flex-col items-center gap-6 py-12'>
        <CheckCircle size={64} className='text-success' strokeWidth={1.5} />
        <div className='text-center'>
          <h2 className='text-2xl font-bold text-foreground'>Import Complete</h2>
          <p className='text-muted-foreground mt-2'>
            {stats.committed > 0
              ? `${stats.committed} records have been committed to your usage data.`
              : 'Your data has been submitted to staging.'}
          </p>
        </div>

        <div className='flex flex-wrap justify-center gap-3'>
          <Card className='bg-card w-40'>
            <CardContent className='p-4 text-center'>
              <p className='text-2xl font-bold'>{stats.total}</p>
              <p className='text-xs text-muted-foreground'>Total Rows</p>
            </CardContent>
          </Card>
          <Card className='bg-card w-40'>
            <CardContent className='p-4 text-center'>
              <p className='text-2xl font-bold text-success'>{stats.committed || stats.matched}</p>
              <p className='text-xs text-success'>Committed</p>
            </CardContent>
          </Card>
          <Card className='bg-card w-40'>
            <CardContent className='p-4 text-center'>
              <p className='text-2xl font-bold text-warning'>{stats.duplicates}</p>
              <p className='text-xs text-warning'>Duplicates</p>
            </CardContent>
          </Card>
          {stats.errors > 0 && (
            <Card className='bg-card w-40'>
              <CardContent className='p-4 text-center'>
                <p className='text-2xl font-bold text-danger'>{stats.errors}</p>
                <p className='text-xs text-danger'>Errors</p>
              </CardContent>
            </Card>
          )}
        </div>

        <div className='flex gap-3'>
          <Button variant='outline' onClick={() => history.push('/usage')}>
            <FileText size={16} strokeWidth={1.5} />
            View Usage Data
          </Button>
          <Button variant='outline' onClick={() => history.push('/reports')}>
            <BarChart3 size={16} strokeWidth={1.5} />
            Go to Reports
          </Button>
          <Button onClick={() => history.push('/intelligent-import')}>
            <ArrowRight size={16} strokeWidth={1.5} />
            Import Another File
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-4'>
      <Card className='bg-card'>
        <CardHeader>
          <CardTitle className='text-lg'>Ready to Commit</CardTitle>
        </CardHeader>
        <CardContent className='flex flex-col gap-4'>
          <Alert>
            <AlertCircle size={16} />
            <AlertDescription>
              This will write {stats.matched} matched records to your usage data.
              This action is recorded in the audit trail but can be reversed from the staging review.
            </AlertDescription>
          </Alert>

          <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
            <div className='p-3 bg-muted rounded-md text-center'>
              <p className='text-xl font-bold'>{stats.total}</p>
              <p className='text-xs text-muted-foreground'>Total Rows</p>
            </div>
            <div className='p-3 bg-muted rounded-md text-center'>
              <p className='text-xl font-bold text-success'>{stats.matched}</p>
              <p className='text-xs text-success'>Matched</p>
            </div>
            <div className='p-3 bg-muted rounded-md text-center'>
              <p className='text-xl font-bold text-warning'>{stats.duplicates}</p>
              <p className='text-xs text-warning'>Duplicates Skipped</p>
            </div>
            <div className='p-3 bg-muted rounded-md text-center'>
              <p className='text-xl font-bold text-muted-foreground'>{stats.filtered}</p>
              <p className='text-xs text-muted-foreground'>Filtered</p>
            </div>
          </div>

          <div className='flex justify-end gap-3'>
            <Button
              variant='outline'
              onClick={() => history.push('/intelligent-import')}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCommit}
              loading={loading?.commit}
            >
              <CheckCircle size={16} strokeWidth={1.5} />
              Commit to Usage Data
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const mapStateToProps = (state) => ({
  loading: state.intelligentImport?.loading || {},
});

const mapDispatchToProps = {
  commitImport: IMPORT_ACTIONS.commitImport,
};

export default connect(mapStateToProps, mapDispatchToProps)(StagingSubmit);
